import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { passesApi } from '@/shared/api';
import type {
  CreatePassRequest,
  UpdatePassRequest,
  CreatePassResponse,
  UpdatePassResponse,
} from '@/shared/api';
import { actionLogsKeys, clientsKeys } from '@/shared/lib/queryKeys';
import { emitToast } from '@/shared/lib/toastEvents';
import { track } from '@/shared/observability';

// Mutations
export function useCreatePass(
  options?: UseMutationOptions<
    CreatePassResponse,
    Error,
    { clientId: string; data: CreatePassRequest },
    unknown
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    meta: { errorToast: 'Failed to create pass. Contact support.', ...options?.meta },
    mutationFn: ({ clientId, data }: { clientId: string; data: CreatePassRequest }) =>
      passesApi.create(clientId, data),
    onSuccess: async (data, variables, onMutateResult, context) => {
      track('pass_created', {
        unlimited: String(variables.data.maxCredits === null),
        hasDuration: String(Boolean(variables.data.endsAt)),
      });
      emitToast({
        type: 'success',
        message: `Pass created: ${variables.data.name}`,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: clientsKeys.byId(variables.clientId) }),
        queryClient.invalidateQueries({ queryKey: clientsKeys.list() }),
        queryClient.invalidateQueries({ queryKey: actionLogsKeys.byClient(variables.clientId) }),
      ]);
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useUpdatePass(
  options?: UseMutationOptions<
    UpdatePassResponse,
    Error,
    { id: string; data: UpdatePassRequest; clientId: string },
    unknown
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    meta: { errorToast: 'Failed to update pass. Contact support.', ...options?.meta },
    mutationFn: ({ id, data }) => passesApi.update(id, data),
    onSuccess: async (data, variables, onMutateResult, context) => {
      emitToast({
        type: 'success',
        message: `Pass updated: ${variables.data.name}`,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: clientsKeys.byId(variables.clientId) }),
        queryClient.invalidateQueries({ queryKey: clientsKeys.list() }),
        queryClient.invalidateQueries({ queryKey: actionLogsKeys.byClient(variables.clientId) }),
      ]);
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

export function useDeletePass(
  options?: UseMutationOptions<void, Error, { id: string; clientId: string }, unknown>
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    meta: { errorToast: 'Failed to delete pass. Contact support.', ...options?.meta },
    mutationFn: ({ id }) => passesApi.delete(id),
    onSuccess: async (data, variables, onMutateResult, context) => {
      emitToast({
        type: 'success',
        message: 'Pass deleted successfully',
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: clientsKeys.byId(variables.clientId) }),
        queryClient.invalidateQueries({ queryKey: clientsKeys.list() }),
        queryClient.invalidateQueries({ queryKey: actionLogsKeys.byClient(variables.clientId) }),
      ]);
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
